// Ad-platform conversions in the browser (docs/ADS.md). The server queues sign-up and
// purchase conversions; they reach the tags (/ads-tags.js, window.teambridgeAds) from:
// - the register response, fired before its redirect (often straight to Stripe);
// - every session load (src/api.js);
// - /team after a Stripe checkout: the webhook may land after the page loads, so the
//   page polls GET /api/user/ad-conversions until the purchase is there.
// Nothing happens when the tags are off (TEAMBRIDGE_ADS_TAGS, EU/UK visitors, other hosts).

export const FIRE_WAIT_MS = 2000; // The tags call back within 1.5 s; this covers a tag that never does.
export const PURCHASE_POLL_MS = 1500;
export const PURCHASE_POLL_MAX_MS = 20000;
const firedKinds = new Set();
let purchaseWatch = null;
const tags = () => typeof window !== 'undefined' ? window.teambridgeAds : null;
export const adTagsEnabled = () => Boolean(tags()?.enabled);

// Fires the conversions; resolves once their hits are sent, or after FIRE_WAIT_MS. Never rejects.
export function fireAdConversions(list, {
  waitMs = FIRE_WAIT_MS
} = {}) {
  const ads = tags();
  if (!Array.isArray(list) || !list.length || !ads?.enabled) return Promise.resolve();
  list.forEach(item => item?.kind && firedKinds.add(item.kind));
  return new Promise(resolve => {
    const timer = setTimeout(resolve, waitMs);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    try {
      ads.fire(list, done);
    } catch (_) {
      done();
    }
  });
}

// After a checkout: polls until the purchase conversion has fired (here or by a session
// load) or PURCHASE_POLL_MAX_MS passed. One watch per page, whatever mounts or navigates.
export function watchPurchaseConversion(fetchPending, {
  intervalMs = PURCHASE_POLL_MS,
  maxMs = PURCHASE_POLL_MAX_MS,
  now = () => Date.now()
} = {}) {
  if (!adTagsEnabled()) return Promise.resolve(false);
  if (purchaseWatch) return purchaseWatch;
  const until = now() + maxMs;
  purchaseWatch = (async () => {
    while (!firedKinds.has('purchase')) {
      try {
        const response = await fetchPending();
        await fireAdConversions(response?.adConversions);
      } catch (_) {/* Try again on the next tick. */}
      if (firedKinds.has('purchase') || now() + intervalMs > until) break;
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return firedKinds.has('purchase');
  })();
  return purchaseWatch;
}

// Tests only.
export function resetAdConversionsForTest() {
  firedKinds.clear();
  purchaseWatch = null;
}